/**
 * Validate an IANA timezone identifier (e.g. `America/Denver`).
 *
 * Uses `Intl.DateTimeFormat` so invalid zones fail closed the same way on
 * Node and browsers.
 */
export function isValidTimezone(timezone: string): boolean {
	try {
		Intl.DateTimeFormat(undefined, { timeZone: timezone });
		return true;
	} catch {
		return false;
	}
}

/** "YYYY-MM" of the month an instant falls in, in `zone`. */
export function monthKeyInZone(at: Date, zone: string): string {
	const { month, year } = zonedParts(at, zone);
	return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`;
}

/**
 * The inclusive window of a "YYYY-MM" month in `zone`: midnight on the 1st
 * through the last millisecond of its last day (11:59:59.999 PM).
 */
export function zonedMonthWindow(
	key: string,
	zone: string,
): { from: Date; to: Date } {
	const year = Number(key.slice(0, 4));
	const monthIndex = Number(key.slice(5, 7)) - 1;

	return {
		from: zonedMonthStart(year, monthIndex, zone),
		to: new Date(zonedMonthStart(year, monthIndex + 1, zone).getTime() - 1),
	};
}

/**
 * The instant of local midnight on the 1st of a month in `zone`. `monthIndex`
 * is 0-based and may overflow into the next year.
 *
 * Guesses with the offset at UTC midnight, then corrects with the offset at
 * the guess, so a DST change between the two lands right. US zones change at
 * 2 AM, so midnight always exists exactly once.
 */
function zonedMonthStart(year: number, monthIndex: number, zone: string): Date {
	const utcMidnight = Date.UTC(year, monthIndex, 1);
	const guess = utcMidnight - zoneOffsetMs(new Date(utcMidnight), zone);
	return new Date(utcMidnight - zoneOffsetMs(new Date(guess), zone));
}

/** Calendar parts of an instant in `zone`. */
function zonedParts(at: Date, zone: string) {
	const parts = new Intl.DateTimeFormat("en-US", {
		day: "numeric",
		hour: "numeric",
		hourCycle: "h23",
		minute: "numeric",
		month: "numeric",
		second: "numeric",
		timeZone: zone,
		year: "numeric",
	}).formatToParts(at);
	const read = (type: string): number =>
		Number(parts.find((part) => part.type === type)?.value ?? 0);

	return {
		day: read("day"),
		// `hourCycle: "h23"` should never yield 24, but some ICU builds have.
		hour: read("hour") % 24,
		minute: read("minute"),
		month: read("month"),
		second: read("second"),
		year: read("year"),
	};
}

/** Milliseconds `zone` is ahead of UTC at `at` (negative west of Greenwich). */
function zoneOffsetMs(at: Date, zone: string): number {
	const p = zonedParts(at, zone);
	const asUtc = Date.UTC(
		p.year,
		p.month - 1,
		p.day,
		p.hour,
		p.minute,
		p.second,
	);
	return asUtc - Math.floor(at.getTime() / 1000) * 1000;
}
